import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ValuesDesk, {
    DeskRibbon,
    DeskPanel,
    DeskTitle,
    DeskNote,
    DeskField,
    DeskEmpty,
    DESK_INPUT_CLASS,
    DESK_BUTTON_CLASS,
    DESK_PRIMARY_CLASS,
    DESK_PRIMARY_STYLE,
} from '../../../components/staff/values/ValuesDesk';
import {
    FurniThumb,
    PIXEL_IMAGE_STYLE,
    PLACEHOLDER_LARGE,
    formatNumber,
    relativeFromMinutes,
    bobbaSearchUrl,
} from '../../../components/staff/values/valuesShared';
import ConfirmModal from '../../../components/ConfirmModal';
import {
    getHabboFurni,
    getAllCategories,
    updateHabboFurni,
    addValue,
    getValueHistory,
    deleteValue,
    getBobbaValue,
    getFurniIndexInfo,
} from '../../../services/api';

const BOBBA_RARES_URL = 'https://bobba.me/rares';

const FURNI_INDEX_URL = 'https://furniindex.com';

const GOLD_BAR_RATE = 50;

const LATEST_LIMIT = 60;

const normalise = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const detailsFromItem = (item) => ({
    info: item.info || '',
    categoryId: item.categoryId || '',
    releaseDate: item.releaseDate || '',
    removeDate: item.removeDate || '',
    releasePrice: item.releasePrice === null || item.releasePrice === undefined ? '' : String(item.releasePrice),
});

const missingForPublic = (details) => [
    !details.categoryId && 'a category',
    !details.releaseDate && 'a release date',
].filter(Boolean);

function PublicBadge({ item }) {
    const missing = missingForPublic(item);

    return missing.length === 0 ? (
        <span className="border border-green-300 bg-green-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-green-700">
            On values page
        </span>
    ) : (
        <span
            className="border border-orange-300 bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-orange-800"
            title={'Needs ' + missing.join(' and ') + ' to show on the values page'}
        >
            Hidden
        </span>
    );
}

const formatDateForDisplay = (iso) => {
    const parts = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return parts ? parts[3] + '/' + parts[2] + '/' + parts[1] : iso;
};

function useBobba(name, code) {
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!name && !code) {
            setData(null);
            return undefined;
        }

        let active = true;
        setData(null);

        getBobbaValue({ code, name })
            .then(response => { if (active) setData(response); })
            .catch(() => { if (active) setData({ available: false, searchUrl: bobbaSearchUrl(name || code) }); });

        return () => { active = false; };
    }, [name, code]);

    return data;
}

function useFurniIndex(name, code) {
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!name && !code) {
            setData(null);
            return undefined;
        }

        let active = true;
        setData(null);

        getFurniIndexInfo({ name, code })
            .then(response => { if (active) setData(response); })
            .catch(() => { if (active) setData({ matched: false }); });

        return () => { active = false; };
    }, [name, code]);

    return data;
}

const describePrice = (value, index) => {
    const priceDate = index?.suggestions?.releasePriceDate;
    const firstDate = index?.suggestions?.releaseDate;
    const label = formatNumber(value) + ' credits';

    return priceDate && priceDate !== firstDate
        ? label + ' (from the ' + formatDateForDisplay(priceDate) + ' release)'
        : label;
};

function IndexSuggestion({ value, current, display, multiline, onUse }) {
    if (value === null || value === undefined || value === '') return null;
    if (String(value) === String(current ?? '')) return null;

    return (
        <div className="mb-1 border border-amber-300 bg-amber-50 px-2 py-1">
            <span className="block font-['Jersey_10'] text-[12px] uppercase leading-4 tracking-[1px] text-amber-800">
                FurniIndex
            </span>

            <div className="flex items-center gap-2">
                <span
                    className={'min-w-0 flex-1 text-[12px] text-gray-700 ' + (multiline ? '' : 'truncate')}
                    title={String(value)}
                >
                    {display ? display(value) : String(value)}
                </span>
                <button
                    type="button"
                    onClick={() => onUse(value)}
                    className={DESK_BUTTON_CLASS + ' shrink-0 py-[2px] text-[12px]'}
                >
                    {current === null || current === undefined || current === '' ? 'Add it' : 'Use it'}
                </button>
            </div>
        </div>
    );
}

function describeRelease(release) {
    if (release.costType === 'Credits' && release.cost !== null) {
        return formatNumber(release.cost) + ' credits';
    }

    const parts = [];
    if (release.costType && release.costType !== 'Other') parts.push(release.costType);
    if (release.lootboxName) parts.push(release.lootboxName);
    if (release.npcTrader) parts.push(release.npcTrader);
    if (release.currency) parts.push(release.currency);
    if (release.npcSold !== null && release.npcAvailability !== null) {
        parts.push(release.npcSold + ' of ' + release.npcAvailability + ' sold');
    }

    return parts.length > 0 ? parts.join(' \u00b7 ') : 'Details not recorded';
}

function ReleaseHistory({ index }) {
    if (!index?.matched || index.releases.length === 0) return null;

    const total = index.releases.length;

    return (
        <div className="border border-gray-200 bg-white">
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-200 bg-gray-50 px-2 py-1">
                <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-700">
                    FurniIndex release history
                </span>
                <span className="text-[11px] text-gray-500">
                    Released {total} time{total === 1 ? '' : 's'}
                    {index.tradeable ? ', tradeable' : ', not tradeable'}
                </span>
            </div>

            <ul className="max-h-[170px] overflow-y-auto">
                {index.releases.map((release, i) => (
                    <li key={i} className="flex gap-2 border-b border-gray-100 px-2 py-1 last:border-b-0">
                        <span className="w-[150px] shrink-0 text-[11px] font-bold text-gray-800">
                            {formatDateForDisplay(release.releaseDate) || 'Unknown'}
                            {release.removedDate ? ' to ' + formatDateForDisplay(release.removedDate) : ''}
                        </span>
                        <span className="min-w-0 flex-1 text-[11px] text-gray-600">{describeRelease(release)}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function CategorySuggestion({ index, categories, current, onUse }) {
    const suggested = useMemo(() => {
        const wanted = (index?.categories || []).map(normalise);
        if (wanted.length === 0) return null;
        return categories.find(category => wanted.includes(normalise(category.name))) || null;
    }, [index, categories]);

    if (!suggested || suggested.id === current) return null;

    const currentName = categories.find(category => category.id === current)?.name || '';

    return (
        <IndexSuggestion value={suggested.name} current={currentName} onUse={() => onUse(suggested.id)} />
    );
}

function BobbaBox({ bobba, name, ourAverage }) {
    if (!bobba) {
        return <div className="border border-gray-200 bg-gray-50 p-3 text-[12px] text-gray-400">Checking bobba.me…</div>;
    }

    if (!bobba.available) {
        return (
            <div className="border border-gray-200 bg-gray-50 p-3 text-[12px] text-gray-500">
                bobba.me could not be reached right now.
                <a href={bobba.searchUrl} target="_blank" rel="noreferrer" className="ml-1 text-maroon underline">
                    Open it manually
                </a>
            </div>
        );
    }

    if (!bobba.match) {
        return (
            <div className="border border-gray-200 bg-gray-50 p-3 text-[12px] text-gray-500">
                No matching furni on bobba.me. Their name for it probably differs.
                <a href={bobba.searchUrl} target="_blank" rel="noreferrer" className="ml-1 text-maroon underline">
                    Search their list
                </a>
            </div>
        );
    }

    const theirs = bobba.match.habloons;
    const difference = theirs !== null && ourAverage ? theirs - ourAverage : null;
    const percent = difference !== null && ourAverage ? Math.round((difference / ourAverage) * 100) : null;

    return (
        <div className="border border-gray-200 bg-white p-3">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <span className="font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gray-700">Bobba.me says</span>
                <a
                    href={bobba.match.url || bobba.searchUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={DESK_BUTTON_CLASS}
                    title={'Open ' + (name || bobba.match.label) + ' on bobba.me in a new tab'}
                >
                    {name || bobba.match.label} on bobba.me
                </a>
            </div>

            <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-['Jersey_10'] text-[24px] leading-none text-gray-900">
                    {theirs === null ? 'No value' : formatNumber(theirs)}
                </span>
                <span className="text-[12px] text-gray-500">habloons</span>

                {difference !== null && (
                    <span
                        className={
                            'border px-1.5 py-0.5 text-[10px] font-bold uppercase '
                            + (difference > 0
                                ? 'border-green-300 bg-green-100 text-green-700'
                                : difference < 0
                                    ? 'border-red-300 bg-red-100 text-red-700'
                                    : 'border-gray-300 bg-gray-100 text-gray-600')
                        }
                    >
                        {difference === 0
                            ? 'Same as ours'
                            : (difference > 0 ? '+' : '−') + formatNumber(Math.abs(difference)) + ' vs ours (' + percent + '%)'}
                    </span>
                )}
            </div>

            <div className="mt-1 text-[11px] text-gray-400">
                Matched on “{bobba.match.label}” · their last update {bobba.match.updated || 'unknown'}
            </div>

        </div>
    );
}

function ValueForm({ item, currentValue, bobba, onSaved }) {
    const [minValue, setMinValue] = useState('');
    const [maxValue, setMaxValue] = useState('');
    const [currency, setCurrency] = useState('habloons');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setMinValue(currentValue ? String(currentValue.min) : '');
        setMaxValue(currentValue ? String(currentValue.max) : '');
        setCurrency('habloons');
    }, [item.id, currentValue]);

    const multiplier = currency === 'goldbars' ? GOLD_BAR_RATE : 1;
    const previewMin = minValue === '' ? null : Math.round(Number(minValue) * multiplier);
    const previewMax = maxValue === '' ? null : Math.round(Number(maxValue) * multiplier);
    const previewAverage = previewMin === null || previewMax === null
        ? null
        : Math.round((previewMin + previewMax) / 2);

    const currentAverage = currentValue ? currentValue.average : null;
    const change = previewAverage !== null && currentAverage !== null ? previewAverage - currentAverage : null;

    const changeCurrency = (next) => {
        if (next === currency) return;

        const factor = next === 'goldbars' ? 1 / GOLD_BAR_RATE : GOLD_BAR_RATE;
        const convert = (raw) => {
            if (raw === '') return '';
            const n = Number(raw);
            if (!Number.isFinite(n)) return raw;
            return String(Math.round(n * factor * 100) / 100);
        };

        setMinValue(convert(minValue));
        setMaxValue(convert(maxValue));
        setCurrency(next);
    };

    const submit = async (e) => {
        e.preventDefault();

        if (minValue === '' || maxValue === '') {
            toast.error('Enter both a minimum and a maximum');
            return;
        }

        const finalMin = Math.round(Number(minValue) * multiplier);
        const finalMax = Math.round(Number(maxValue) * multiplier);

        if (!Number.isFinite(finalMin) || !Number.isFinite(finalMax) || finalMin < 0 || finalMax < 0) {
            toast.error('Values must be zero or higher');
            return;
        }

        if (finalMin > finalMax) {
            toast.error('The minimum cannot be higher than the maximum');
            return;
        }

        setSaving(true);
        try {
            const response = await addValue(item.id, finalMin, finalMax);
            if (response.success) {
                toast.success('Saved ' + formatNumber(finalMin) + ' - ' + formatNumber(finalMax) + ' habloons');
                onSaved();
            } else {
                toast.error(response.message || 'Failed to save that value');
            }
        } catch {
            toast.error('Failed to save that value');
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <DeskField label="Minimum" required hint={currency === 'goldbars' ? 'gold bars' : 'habloons'}>
                    <input
                        type="number"
                        min="0"
                        step={currency === 'goldbars' ? 'any' : '1'}
                        value={minValue}
                        onChange={(e) => setMinValue(e.target.value)}
                        className={DESK_INPUT_CLASS}
                        placeholder="Lowest seen"
                    />
                </DeskField>

                <DeskField label="Maximum" required hint={currency === 'goldbars' ? 'gold bars' : 'habloons'}>
                    <input
                        type="number"
                        min="0"
                        step={currency === 'goldbars' ? 'any' : '1'}
                        value={maxValue}
                        onChange={(e) => setMaxValue(e.target.value)}
                        className={DESK_INPUT_CLASS}
                        placeholder="Highest seen"
                    />
                </DeskField>

                <DeskField label="Entered in" hint="always stored as habloons">
                    <select
                        value={currency}
                        onChange={(e) => changeCurrency(e.target.value)}
                        className={DESK_INPUT_CLASS}
                    >
                        <option value="habloons">Habloons</option>
                        <option value="goldbars">Gold bars (1 = 50 habloons)</option>
                    </select>
                </DeskField>
            </div>

            <div className="border border-gray-200 bg-gray-50 px-3 py-2 text-[12px] text-gray-600">
                {previewAverage === null ? (
                    'Fill both boxes to see what will be saved.'
                ) : (
                    <>
                        Saving <strong>{formatNumber(previewMin)} - {formatNumber(previewMax)}</strong> habloons
                        {' '}(average <strong>{formatNumber(previewAverage)}</strong>)
                        {change !== null && change !== 0 && (
                            <span className={change > 0 ? ' text-green-700' : ' text-red-600'}>
                                {', '}that is {change > 0 ? 'up' : 'down'} {formatNumber(Math.abs(change))} on the current average
                            </span>
                        )}
                        {change === 0 && ', no change on the current average'}
                    </>
                )}
            </div>

            <BobbaBox bobba={bobba} name={item.name} ourAverage={currentAverage} />

            <div className="flex items-center gap-2">
                <button type="submit" disabled={saving} className={DESK_PRIMARY_CLASS} style={DESK_PRIMARY_STYLE}>
                    {saving ? 'Saving…' : 'Save new value'}
                </button>
                <DeskNote>Saved values post to Discord and notify anyone subscribed to this furni.</DeskNote>
            </div>
        </form>
    );
}

function DetailsForm({ item, categories, index, onSaved }) {
    const [details, setDetails] = useState(() => detailsFromItem(item));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setDetails(detailsFromItem(item));
    }, [item]);

    const set = (field) => (e) => setDetails(prev => ({ ...prev, [field]: e.target.value }));

    const missing = missingForPublic(details);

    const submit = async (e) => {
        e.preventDefault();

        if (details.releasePrice !== '' && (!Number.isInteger(Number(details.releasePrice)) || Number(details.releasePrice) < 0)) {
            toast.error('The release price must be a whole number of credits');
            return;
        }

        setSaving(true);
        try {
            const response = await updateHabboFurni(item.id, {
                info: details.info || null,
                categoryId: details.categoryId || null,
                releaseDate: details.releaseDate || null,
                removeDate: details.removeDate || null,
                releasePrice: details.releasePrice === '' ? null : Number(details.releasePrice),
            });

            if (response.success) {
                toast.success(response.message || 'Details saved');
                onSaved();
            } else {
                toast.error(response.message || 'Failed to save the details');
            }
        } catch {
            toast.error('Failed to save the details');
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="flex flex-col gap-3">
            <div
                className={
                    'border px-3 py-2 text-[12px] '
                    + (missing.length === 0
                        ? 'border-green-300 bg-green-50 text-green-800'
                        : 'border-orange-300 bg-orange-50 text-orange-800')
                }
            >
                {missing.length === 0
                    ? 'Once saved, this furni shows on the public values page.'
                    : 'Needs ' + missing.join(' and ') + ' before it shows on the public values page.'}
            </div>

            <DeskField label="Category" required>
                <CategorySuggestion
                    index={index}
                    categories={categories}
                    current={details.categoryId}
                    onUse={(id) => setDetails(prev => ({ ...prev, categoryId: id }))}
                />
                <select value={details.categoryId || ''} onChange={set('categoryId')} className={DESK_INPUT_CLASS}>
                    <option value="">Uncategorised</option>
                    {categories.map(category => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                </select>
            </DeskField>

            <ReleaseHistory index={index} />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <DeskField label="Released" required>
                    <IndexSuggestion
                        value={index?.suggestions?.releaseDate}
                        current={details.releaseDate}
                        display={formatDateForDisplay}
                        onUse={(value) => setDetails(prev => ({ ...prev, releaseDate: String(value) }))}
                    />
                    <input type="date" value={details.releaseDate} onChange={set('releaseDate')} className={DESK_INPUT_CLASS} />
                </DeskField>

                <DeskField label="Removed" hint="leave blank if still available">
                    <IndexSuggestion
                        value={index?.suggestions?.removeDate}
                        current={details.removeDate}
                        display={formatDateForDisplay}
                        onUse={(value) => setDetails(prev => ({ ...prev, removeDate: String(value) }))}
                    />
                    <input type="date" value={details.removeDate} onChange={set('removeDate')} className={DESK_INPUT_CLASS} />
                </DeskField>

                <DeskField label="Release price" hint="credits">
                    <IndexSuggestion
                        value={index?.suggestions?.releasePrice}
                        current={details.releasePrice}
                        display={(value) => describePrice(value, index)}
                        onUse={(value) => setDetails(prev => ({ ...prev, releasePrice: String(value) }))}
                    />
                    <input
                        type="number"
                        min="0"
                        value={details.releasePrice}
                        onChange={set('releasePrice')}
                        className={DESK_INPUT_CLASS}
                    />
                </DeskField>
            </div>

            <DeskField label="Notes" hint="optional trivia shown on the public page">
                <IndexSuggestion
                    value={index?.suggestions?.info}
                    current={details.info}
                    multiline
                    onUse={(value) => setDetails(prev => ({ ...prev, info: String(value) }))}
                />
                <textarea value={details.info} onChange={set('info')} rows="3" className={DESK_INPUT_CLASS} />
            </DeskField>

            <div className="border border-gray-200 bg-gray-50 p-3">
                <span className="font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gray-700">Images</span>
                <div className="mt-2 flex items-center gap-2">
                    <FurniThumb src={item.largeImg} alt={item.name} size={56} fallback={PLACEHOLDER_LARGE} />
                    <FurniThumb src={item.smallImg} alt={item.name} size={56} />
                    <FurniThumb src={item.iconImg} alt={item.name} size={56} />
                    <DeskNote>
                        Large, small and icon from FurniIndex. Filled in automatically once FurniIndex has renders.
                    </DeskNote>
                </div>
            </div>

            <div>
                <button type="submit" disabled={saving} className={DESK_PRIMARY_CLASS} style={DESK_PRIMARY_STYLE}>
                    {saving ? 'Saving…' : 'Save details'}
                </button>
            </div>
        </form>
    );
}

function HistoryTable({ history, loading, onDelete }) {
    if (loading) return <DeskEmpty>Loading history…</DeskEmpty>;
    if (history.length === 0) return <DeskEmpty>No values recorded yet</DeskEmpty>;

    return (
        <div className="overflow-x-auto">
            <table className="w-full">
                <thead>
                    <tr className="border-b-2 border-gray-300 bg-gray-100">
                        <th className="px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px]">Range</th>
                        <th className="px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px]">Average</th>
                        <th className="hidden px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px] sm:table-cell">Set by</th>
                        <th className="px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px]">When</th>
                        <th className="px-3 py-2" />
                    </tr>
                </thead>
                <tbody>
                    {history.map(entry => (
                        <tr key={entry.id} className={'border-b border-gray-200 ' + (entry.deleted ? 'bg-red-50' : '')}>
                            <td className={'px-3 py-2 text-[13px] font-bold ' + (entry.deleted ? 'text-red-600 line-through' : 'text-gray-900')}>
                                {formatNumber(entry.min)} - {formatNumber(entry.max)}
                            </td>
                            <td className={'px-3 py-2 text-[13px] font-bold ' + (entry.deleted ? 'text-red-600 line-through' : 'text-blue-600')}>
                                {formatNumber(entry.average)}
                            </td>
                            <td className="hidden px-3 py-2 text-[12px] text-gray-600 sm:table-cell">{entry.updatedBy}</td>
                            <td className="px-3 py-2 text-[12px] text-gray-500">{entry.updatedText || entry.updatedTimestamp}</td>
                            <td className="px-3 py-2 text-right">
                                {entry.deleted ? (
                                    <span className="text-[11px] font-bold uppercase text-red-600">Deleted</span>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => onDelete(entry.id)}
                                        className={DESK_BUTTON_CLASS + ' text-red-700'}
                                    >
                                        Delete
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function SearchResults({ results, query, onSelect }) {
    if (results.length === 0) {
        return (
            <div className="p-6 text-center">
                <p className="mb-1 font-['Jersey_10'] text-[18px] uppercase tracking-[1px] text-gray-700">
                    {query.trim() ? 'Nothing matches “' + query.trim() + '”' : 'No furni found yet'}
                </p>
                <p className="text-[12px] text-gray-500">
                    New furni are added automatically as soon as they appear on FurniIndex.
                </p>
            </div>
        );
    }

    return (
        <ul>
            {results.map(item => (
                <li key={item.id}>
                    <button
                        type="button"
                        onClick={() => onSelect(item.id)}
                        className="flex w-full items-center gap-3 border-b border-gray-200 px-3 py-2 text-left transition-colors hover:bg-gray-50"
                    >
                        <FurniThumb src={item.iconImg || item.smallImg} alt={item.name} size={40} />

                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <span className="truncate text-[13px] font-bold text-gray-900">{item.name}</span>
                                <PublicBadge item={item} />
                            </div>
                            <div className="truncate text-[11px] text-gray-400">
                                {item.code}{item.categoryName ? ' · ' + item.categoryName : ''}
                                {!query.trim() && item.addedText ? ' · found ' + item.addedText : ''}
                            </div>
                        </div>

                        <div className="text-right">
                            <div className="text-[12px] font-bold text-gray-900">
                                {item.min === null || item.min === undefined
                                    ? 'No value'
                                    : formatNumber(item.min) + ' - ' + formatNumber(item.max)}
                            </div>
                            <div className="text-[11px] text-gray-400">{relativeFromMinutes(item.minutesSince)}</div>
                        </div>
                    </button>
                </li>
            ))}
        </ul>
    );
}

function FurniDesk({ userData }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedId = searchParams.get('item') || '';

    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState({ open: false, valueId: null });
    const searchRef = useRef(null);

    const loadItems = useCallback(async () => {
        try {
            const [itemsResponse, categoriesResponse] = await Promise.all([getHabboFurni(), getAllCategories()]);
            if (itemsResponse.success) setItems(itemsResponse.items);
            if (categoriesResponse.success) setCategories(categoriesResponse.categories);
        } catch {
            toast.error('Failed to load the furni list');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    const selected = useMemo(
        () => items.find(item => item.id === selectedId) || null,
        [items, selectedId]
    );

    const furniIndex = useFurniIndex(selected?.name, selected?.code);
    const bobba = useBobba(selected?.name, selected?.code);

    const loadHistory = useCallback(async (furniId) => {
        setHistoryLoading(true);
        try {
            const response = await getValueHistory(furniId);
            setHistory(response.success ? response.values : []);
        } catch {
            setHistory([]);
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!selectedId) {
            setHistory([]);
            return;
        }
        loadHistory(selectedId);
    }, [selectedId, loadHistory]);

    // No search: the newest furni found (the API returns them newest first)
    const results = useMemo(() => {
        const term = normalise(query);
        if (!term) return items.slice(0, LATEST_LIMIT);
        return items
            .filter(item => normalise(item.name).includes(term) || normalise(item.code).includes(term))
            .slice(0, LATEST_LIMIT);
    }, [items, query]);

    if (!userData?.hasStaffRole) return <Navigate to="/" replace />;

    const currentValue = history.find(entry => !entry.deleted) || null;

    const openItem = (id) => {
        setSearchParams(id ? { item: id } : {});
        setQuery('');
    };

    const backToSearch = () => {
        setSearchParams({});
        setTimeout(() => searchRef.current?.focus(), 0);
    };

    const handleDeleteValue = async () => {
        try {
            const response = await deleteValue(confirmDelete.valueId);
            if (response.success) {
                toast.success('Value deleted');
                loadHistory(selectedId);
                loadItems();
            } else {
                toast.error(response.message || 'Failed to delete that value');
            }
        } catch {
            toast.error('Failed to delete that value');
        } finally {
            setConfirmDelete({ open: false, valueId: null });
        }
    };

    const afterValueSaved = () => {
        loadHistory(selectedId);
        loadItems();
    };

    const afterDetailsSaved = () => {
        loadItems();
    };

    const docTab = selected ? selected.name : null;

    return (
        <ValuesDesk userData={userData} docTab={docTab}>
            <ConfirmModal
                isOpen={confirmDelete.open}
                onClose={() => setConfirmDelete({ open: false, valueId: null })}
                onConfirm={handleDeleteValue}
                title="Delete this value"
                message="The value is struck through in the history rather than removed. The next newest value becomes the current one."
            />

            <DeskRibbon>
                <DeskTitle>Furni Desk</DeskTitle>

                {selected ? (
                    <>
                        <DeskNote>{selected.code}</DeskNote>
                        <div className="flex-1" />
                        <a
                            href={BOBBA_RARES_URL}
                            target="_blank"
                            rel="noreferrer"
                            className={DESK_BUTTON_CLASS}
                            title="Open the full bobba.me rare values list in a new tab"
                        >
                            Bobba rares
                        </a>
                        <a
                            href={furniIndex?.url || furniIndex?.searchUrl || FURNI_INDEX_URL}
                            target="_blank"
                            rel="noreferrer"
                            className={DESK_BUTTON_CLASS}
                            title={furniIndex?.url ? 'Open this furni on FurniIndex' : 'Search FurniIndex for this furni'}
                        >
                            FurniIndex
                        </a>
                        <button type="button" onClick={backToSearch} className={DESK_BUTTON_CLASS}>
                            Search another furni
                        </button>
                    </>
                ) : (
                    <>
                        <DeskNote>Pick a furni to set its category, release details and value</DeskNote>
                        <div className="flex-1" />
                        <a
                            href={BOBBA_RARES_URL}
                            target="_blank"
                            rel="noreferrer"
                            className={DESK_BUTTON_CLASS}
                            title="Open the full bobba.me rare values list in a new tab"
                        >
                            Bobba rares
                        </a>
                        <a
                            href={FURNI_INDEX_URL}
                            target="_blank"
                            rel="noreferrer"
                            className={DESK_BUTTON_CLASS}
                            title="Open FurniIndex in a new tab"
                        >
                            FurniIndex
                        </a>
                        <input
                            ref={searchRef}
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Name or code…"
                            className="w-[260px] rounded border border-gray-300 px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-maroon"
                        />
                    </>
                )}
            </DeskRibbon>

            {loading ? (
                <DeskPanel title="Loading">
                    <div className="space-y-2">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="h-10 animate-pulse bg-gray-100" />
                        ))}
                    </div>
                </DeskPanel>
            ) : !selected ? (
                <DeskPanel
                    title={query.trim() ? 'Matches (' + results.length + ')' : 'Latest furni found (' + items.length + ' in total)'}
                    padding="p-0"
                >
                    <SearchResults results={results} query={query} onSelect={openItem} />
                </DeskPanel>
            ) : (
                <>
                    <DeskPanel title="Update the value" padding="p-3">
                        <div className="flex flex-col gap-4 lg:flex-row">
                            <div className="flex shrink-0 gap-3">
                                <div className="flex h-[140px] w-[140px] items-center justify-center overflow-hidden border border-gray-200 bg-gray-50">
                                    <img
                                        src={selected.largeImg || PLACEHOLDER_LARGE}
                                        alt={selected.name}
                                        style={PIXEL_IMAGE_STYLE}
                                        onError={(e) => { e.target.src = PLACEHOLDER_LARGE; }}
                                    />
                                </div>

                                <div className="w-[190px]">
                                    <div className="font-['Jersey_10'] text-[22px] leading-tight text-gray-900">{selected.name}</div>
                                    {selected.description && (
                                        <div className="text-[12px] italic text-gray-500">{selected.description}</div>
                                    )}
                                    <div className="mb-2 flex flex-wrap items-center gap-1 text-[11px] text-gray-400">
                                        <span>{selected.code}{selected.categoryName ? ' · ' + selected.categoryName : ''}</span>
                                        <PublicBadge item={selected} />
                                    </div>

                                    {currentValue ? (
                                        <>
                                            <div className="text-[12px] text-gray-500">Currently</div>
                                            <div className="font-['Jersey_10'] text-[22px] leading-none text-gray-900">
                                                {formatNumber(currentValue.min)} - {formatNumber(currentValue.max)}
                                            </div>
                                            <div className="text-[12px] text-gray-500">
                                                average {formatNumber(currentValue.average)} habloons
                                            </div>
                                            <div className="mt-1 text-[11px] text-gray-400">
                                                Set by {currentValue.updatedBy}, {relativeFromMinutes(selected.minutesSince)}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="border border-orange-300 bg-orange-50 px-2 py-1 text-[12px] text-orange-800">
                                            No value has ever been set for this furni.
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex-1">
                                <ValueForm
                                    item={selected}
                                    currentValue={currentValue}
                                    bobba={bobba}
                                    onSaved={afterValueSaved}
                                />
                            </div>
                        </div>
                    </DeskPanel>

                    <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                        <DeskPanel title="Category and release">
                            <DetailsForm
                                item={selected}
                                categories={categories}
                                index={furniIndex}
                                onSaved={afterDetailsSaved}
                            />
                        </DeskPanel>

                        <DeskPanel title={'Value history (' + history.length + ')'} padding="p-0">
                            <HistoryTable
                                history={history}
                                loading={historyLoading}
                                onDelete={(valueId) => setConfirmDelete({ open: true, valueId })}
                            />
                        </DeskPanel>
                    </div>
                </>
            )}
        </ValuesDesk>
    );
}

export default FurniDesk;
